import React, { useState, useEffect } from 'react';
import { Calculator, Save, ChevronRight, ChevronLeft, RefreshCw, PieChart, DollarSign, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { roiCalculatorService } from '../../services/roiCalculatorService';
import { BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrencySymbol } from '../../utils/currencyFormatter';

export default function EcommerceROICalculator() {
  const { profile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'breakdown', 'settings'
  const [hasCompletedWizard, setHasCompletedWizard] = useState(false);
  
  // Configuration state with all questionnaire fields
  const [config, setConfig] = useState({
    id: null,
    configName: 'Business Configuration',
    monthlyRevenue: '',
    profitMarginPercentage: '',
    averageOrderValue: '',
    numberOfProducts: '',
    monthlyShippingCost: '',
    desiredRevenue: '',
    totalPpcBudget: '',
    currency: profile?.currency || 'ZAR',
    isActive: true,
    platformSettings: {
      platform: 'woocommerce',
      hostingCost: '',
      paymentGatewayFeePercentage: '',
      shopifyPlan: null,
      monthlySubscription: '',
      platformFeePercentage: '',
      transactionFeePercentage: ''
    },
    marketingServices: {
      marketingManagementType: 'self_managed',
      monthlyRetainer: '',
      // Checkbox services
      includeOrganicFacebookInstagram: false,
      includePaidMeta: false,
      includePaidGoogleAds: false,
      includePaidMicrosoftAds: false,
      includeNewsletters: false,
      includeWebDev: false,
      additionalOverheads: ''
    },
    financialConfig: {
      vatPercentage: 15.00,
      profitMarginPercentage: '',
      overheadPercentage: ''
    }
  });

  // Shopify plan configurations
  const shopifyPlans = {
    basic: { price: 19, fee: 2.0, label: 'Basic ($19/pm & 2% Transaction Fee)' },
    grow: { price: 49, fee: 1.0, label: 'Grow ($49/pm & 1% Transaction Fee)' },
    advanced: { price: 299, fee: 0.6, label: 'Advanced ($299/pm & 0.6% Transaction Fee)' }
  };

  // Chart colors
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  // Load existing configuration
  useEffect(() => {
    loadConfiguration();
  }, []);

  // Update currency when profile changes
  useEffect(() => {
    if (profile?.currency && config?.currency !== profile?.currency) {
      updateConfig({ currency: profile?.currency });
    }
  }, [profile?.currency]);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setError('');
      const configs = await roiCalculatorService?.getAllConfigs();
      
      const activeConfig = configs?.find(c => c?.isActive) || configs?.[0];
      if (activeConfig) {
        // Merge active config with current profile currency to ensure currency stays synced
        setConfig({
          ...activeConfig,
          currency: profile?.currency || activeConfig?.currency || 'ZAR'
        });
        // If configuration exists with data, skip wizard and show overview
        if (activeConfig?.monthlyRevenue > 0) {
          setHasCompletedWizard(true);
          setCurrentStep(5);
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to load configuration');
      console.error('Error loading configuration:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (config?.id) {
        await roiCalculatorService?.updateConfig(config?.id, config);
      } else {
        const newConfig = await roiCalculatorService?.createConfig(config);
        setConfig(newConfig);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      setError(err?.message || 'Failed to save configuration');
      console.error('Error saving configuration:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (updates) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const updatePlatformSettings = (updates) => {
    setConfig(prev => ({
      ...prev,
      platformSettings: { ...prev?.platformSettings, ...updates }
    }));
  };

  const updateMarketingServices = (updates) => {
    setConfig(prev => ({
      ...prev,
      marketingServices: { ...prev?.marketingServices, ...updates }
    }));
  };

  const handleShopifyPlanChange = (plan) => {
    const planConfig = shopifyPlans?.[plan];
    updatePlatformSettings({
      shopifyPlan: plan,
      monthlySubscription: planConfig?.price,
      transactionFeePercentage: planConfig?.fee
    });
  };

  // Format number with commas and proper spacing using user's currency
  const formatCurrency = (value, currencyCode) => {
    if (!value && value !== 0) return `${getCurrencySymbol(currencyCode || config?.currency)} 0.00`;
    const formatted = parseFloat(value)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${getCurrencySymbol(currencyCode || config?.currency)} ${formatted}`;
  };

  // Format number with commas
  const formatNumberWithCommas = (value) => {
    if (!value && value !== 0) return '';
    // Remove any existing decimals for display purposes
    const wholeNumber = Math.round(parseFloat(value));
    return wholeNumber?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Parse number with commas
  const parseNumberWithCommas = (value) => {
    if (!value) return 0;
    return parseFloat(value?.replace(/,/g, '')) || 0;
  };

  // Handle formatted number input
  const handleFormattedNumberChange = (value, field) => {
    const numericValue = parseNumberWithCommas(value);
    updateConfig({ [field]: numericValue });
  };

  // Calculate cost breakdown
  const calculateCostBreakdown = () => {
    const platform = config?.platformSettings;
    const marketing = config?.marketingServices;
    
    const platformCosts = 
      (parseFloat(platform?.hostingCost) || 0) + 
      (parseFloat(platform?.monthlySubscription) || 0) +
      ((parseFloat(config?.monthlyRevenue) || 0) * (parseFloat(platform?.platformFeePercentage) || 0) / 100) +
      ((parseFloat(config?.monthlyRevenue) || 0) * (parseFloat(platform?.paymentGatewayFeePercentage) || 0) / 100);

    const marketingCosts = (parseFloat(marketing?.monthlyRetainer) || 0);
    const ppcBudget = parseFloat(config?.totalPpcBudget) || 0;

    const additionalCosts = parseFloat(marketing?.additionalOverheads) || 0;
    const shippingCosts = parseFloat(config?.monthlyShippingCost) || 0;
    
    const totalCosts = platformCosts + marketingCosts + ppcBudget + additionalCosts + shippingCosts;
    const revenue = parseFloat(config?.monthlyRevenue) || 0;
    const profitMargin = parseFloat(config?.profitMarginPercentage) || 0;
    const grossProfit = revenue * (profitMargin / 100);
    const netProfit = grossProfit - totalCosts;
    const netProfitMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;
    const roi = totalCosts > 0 ? ((netProfit / totalCosts) * 100)?.toFixed(2) : 0;
    
    const desiredRevenue = parseFloat(config?.desiredRevenue) || 0;
    const revenueGap = desiredRevenue - revenue;

    // Calculate ROAS (Return on Ad Spend) - Revenue generated per dollar spent on ads
    const roas = ppcBudget > 0 ? (revenue / ppcBudget)?.toFixed(2) : 0;
    
    // Calculate if business meets comfortable profit target (20% net profit margin)
    const comfortableProfitTarget = 20; // 20% is considered healthy
    const meetsComfortableTarget = netProfitMargin >= comfortableProfitTarget;

    return {
      platformCosts,
      marketingCosts,
      ppcBudget,
      shippingCosts,
      additionalCosts,
      totalCosts,
      revenue,
      grossProfit,
      netProfit,
      netProfitMargin,
      roi,
      desiredRevenue,
      revenueGap,
      roas,
      comfortableProfitTarget,
      meetsComfortableTarget
    };
  };

  const breakdown = calculateCostBreakdown();

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 5));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const restartWizard = () => {
    setCurrentStep(1);
    setHasCompletedWizard(false);
    setIsEditing(false);
  };

  const completeWizard = async () => {
    await handleSave();
    setHasCompletedWizard(true);
    setCurrentStep(5);
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-auto p-8">
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading configuration...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        
        <main className="flex-1 overflow-auto">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Ecommerce ROI Calculator
              </h1>
              <p className="text-gray-600">
                {!hasCompletedWizard && currentStep < 5 
                  ? 'Answer a few questions to calculate your business ROI' :'Your Business ROI Overview'}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                <p className="font-medium">Error</p>
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {saveSuccess && (
              <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                <p className="text-sm">Configuration saved successfully!</p>
              </div>
            )}

            {/* Progress Bar - Only show during wizard, not after completion */}
            {!hasCompletedWizard && currentStep < 5 && (
              <div className="mb-8">
                <div className="flex justify-between mb-2">
                  {[1, 2, 3, 4]?.map((step) => (
                    <div
                      key={step}
                      className={`flex-1 h-2 mx-1 rounded-full ${
                        step <= currentStep ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-600 text-center">
                  Step {currentStep} of 4
                </p>
              </div>
            )}

            {/* Wizard Steps - Show only if wizard not completed */}
            {!hasCompletedWizard && currentStep < 5 && (
              <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
                {/* Step 1: Business Metrics */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Business Metrics</h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What is your profit margin %?
                      </label>
                      <input
                        type="number"
                        value={config?.profitMarginPercentage || ''}
                        onChange={(e) => updateConfig({ profitMarginPercentage: parseFloat(e?.target?.value) || 0 })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 30"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What is your average order value?
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(config?.averageOrderValue)}
                        onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'averageOrderValue')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 1,500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of products
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(config?.numberOfProducts)}
                        onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'numberOfProducts')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly revenue
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(config?.monthlyRevenue)}
                        onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'monthlyRevenue')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Leave empty if starting fresh"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What is your monthly shipping cost?
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(config?.monthlyShippingCost)}
                        onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'monthlyShippingCost')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 5,000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What is your desired revenue?
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(config?.desiredRevenue)}
                        onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'desiredRevenue')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 5,000,000"
                      />
                    </div>
                  </div>
                )}

                {/* Step 2: Platform Selection */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Platform Configuration</h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select your ecommerce platform
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => updatePlatformSettings({ platform: 'woocommerce' })}
                          className={`p-4 border-2 rounded-lg transition-colors ${
                            config?.platformSettings?.platform === 'woocommerce' ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <p className="font-medium">WooCommerce</p>
                        </button>
                        <button
                          onClick={() => updatePlatformSettings({ platform: 'shopify' })}
                          className={`p-4 border-2 rounded-lg transition-colors ${
                            config?.platformSettings?.platform === 'shopify'
                              ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <p className="font-medium">Shopify</p>
                        </button>
                      </div>
                    </div>

                    {config?.platformSettings?.platform === 'woocommerce' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Hosting fees?
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(config?.platformSettings?.hostingCost)}
                            onChange={(e) => {
                              const value = parseNumberWithCommas(e?.target?.value);
                              updatePlatformSettings({ hostingCost: value });
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 50"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Payment gateway % fee
                          </label>
                          <input
                            type="number"
                            value={config?.platformSettings?.paymentGatewayFeePercentage || ''}
                            onChange={(e) => updatePlatformSettings({ paymentGatewayFeePercentage: parseFloat(e?.target?.value) || 0 })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 2.9"
                          />
                        </div>
                      </>
                    )}

                    {config?.platformSettings?.platform === 'shopify' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Shopify plan
                        </label>
                        <div className="space-y-3">
                          {Object.entries(shopifyPlans)?.map(([key, plan]) => (
                            <button
                              key={key}
                              onClick={() => handleShopifyPlanChange(key)}
                              className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                                config?.platformSettings?.shopifyPlan === key
                                  ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                              }`}
                            >
                              <p className="font-medium">{plan?.label}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Marketing Management */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Marketing Management</h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Do you manage your own marketing or does a marketing agency manage it?
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => updateMarketingServices({ marketingManagementType: 'self_managed' })}
                          className={`p-4 border-2 rounded-lg transition-colors ${
                            config?.marketingServices?.marketingManagementType === 'self_managed' ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <p className="font-medium">Self Managed</p>
                        </button>
                        <button
                          onClick={() => updateMarketingServices({ marketingManagementType: 'agency_managed' })}
                          className={`p-4 border-2 rounded-lg transition-colors ${
                            config?.marketingServices?.marketingManagementType === 'agency_managed' ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <p className="font-medium">Agency Managed</p>
                        </button>
                      </div>
                    </div>

                    {config?.marketingServices?.marketingManagementType === 'agency_managed' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            What is your monthly retainer?
                          </label>
                          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 px-4 py-2 mb-2 rounded">
                            <p className="text-sm font-medium">without Ads cost</p>
                          </div>
                          <input
                            type="text"
                            value={formatNumberWithCommas(config?.marketingServices?.monthlyRetainer)}
                            onChange={(e) => {
                              const value = parseNumberWithCommas(e?.target?.value);
                              updateMarketingServices({ monthlyRetainer: value });
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 5,000"
                          />
                        </div>

                        <div className="border-t pt-6">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Select marketing services included</h3>
                          <div className="space-y-3">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={config?.marketingServices?.includeOrganicFacebookInstagram || false}
                                onChange={(e) => updateMarketingServices({ includeOrganicFacebookInstagram: e?.target?.checked })}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-3 text-sm font-medium text-gray-700">Organic Facebook/Instagram</span>
                            </label>

                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={config?.marketingServices?.includePaidMeta || false}
                                onChange={(e) => updateMarketingServices({ includePaidMeta: e?.target?.checked })}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-3 text-sm font-medium text-gray-700">Paid Meta</span>
                            </label>

                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={config?.marketingServices?.includePaidGoogleAds || false}
                                onChange={(e) => updateMarketingServices({ includePaidGoogleAds: e?.target?.checked })}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-3 text-sm font-medium text-gray-700">Paid Google ADS</span>
                            </label>

                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={config?.marketingServices?.includePaidMicrosoftAds || false}
                                onChange={(e) => updateMarketingServices({ includePaidMicrosoftAds: e?.target?.checked })}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-3 text-sm font-medium text-gray-700">Paid Microsoft ADS</span>
                            </label>

                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={config?.marketingServices?.includeNewsletters || false}
                                onChange={(e) => updateMarketingServices({ includeNewsletters: e?.target?.checked })}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-3 text-sm font-medium text-gray-700">Newsletters</span>
                            </label>

                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={config?.marketingServices?.includeWebDev || false}
                                onChange={(e) => updateMarketingServices({ includeWebDev: e?.target?.checked })}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-3 text-sm font-medium text-gray-700">Web Dev</span>
                            </label>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            What do you spend in PPC budget total?
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(config?.totalPpcBudget)}
                            onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'totalPpcBudget')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 10,000"
                          />
                        </div>
                      </>
                    )}

                    <div className="border-t pt-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Additional overheads
                        </label>
                        <p className="text-sm text-gray-500 mb-2">
                          Add any additional overheads to get an accurate figure on the business ROI
                        </p>
                        <input
                          type="text"
                          value={formatNumberWithCommas(config?.marketingServices?.additionalOverheads)}
                          onChange={(e) => {
                            const value = parseNumberWithCommas(e?.target?.value);
                            updateMarketingServices({ additionalOverheads: value });
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., 1,000"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Cost Breakdown & Review */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Cost Breakdown & Summary</h2>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-blue-900 mb-4">Financial Overview</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Monthly Revenue:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.revenue, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Desired Revenue:</span>
                          <span className="font-semibold text-blue-600">{formatCurrency(breakdown?.desiredRevenue, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Revenue Gap:</span>
                          <span className={`font-semibold ${breakdown?.revenueGap > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                            {formatCurrency(Math.abs(breakdown?.revenueGap), config?.currency)}
                          </span>
                        </div>
                        <div className="border-t border-blue-200 pt-3"></div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Platform Costs:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.platformCosts, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Marketing/Retainer Costs:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.marketingCosts, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">PPC Budget:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.ppcBudget, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Shipping Costs:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.shippingCosts, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Additional Overheads:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.additionalCosts, config?.currency)}</span>
                        </div>
                        <div className="border-t border-blue-300 pt-3 flex justify-between">
                          <span className="text-gray-900 font-semibold">Total Monthly Costs:</span>
                          <span className="font-bold text-lg">{formatCurrency(breakdown?.totalCosts, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-900 font-semibold">Net Profit:</span>
                          <span className={`font-bold text-lg ${breakdown?.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(breakdown?.netProfit, config?.currency)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-900 font-semibold">ROI:</span>
                          <span className={`font-bold text-xl ${breakdown?.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {breakdown?.roi}%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration Summary</h3>
                      <div className="space-y-2 text-sm">
                        <p><span className="font-medium">Platform:</span> {config?.platformSettings?.platform === 'shopify' ? 'Shopify' : 'WooCommerce'}</p>
                        <p><span className="font-medium">Products:</span> {formatNumberWithCommas(config?.numberOfProducts)}</p>
                        <p><span className="font-medium">Average Order Value:</span> {config?.currency} {formatNumberWithCommas(config?.averageOrderValue?.toFixed(2))}</p>
                        <p><span className="font-medium">Marketing Management:</span> {config?.marketingServices?.marketingManagementType === 'agency_managed' ? 'Agency Managed' : 'Self Managed'}</p>
                        {config?.marketingServices?.marketingManagementType === 'agency_managed' && (
                          <div className="mt-3">
                            <p className="font-medium mb-1">Included Services:</p>
                            <ul className="list-disc list-inside space-y-1 text-gray-600">
                              {config?.marketingServices?.includeOrganicFacebookInstagram && <li>Organic Facebook/Instagram</li>}
                              {config?.marketingServices?.includePaidMeta && <li>Paid Meta</li>}
                              {config?.marketingServices?.includePaidGoogleAds && <li>Paid Google ADS</li>}
                              {config?.marketingServices?.includePaidMicrosoftAds && <li>Paid Microsoft ADS</li>}
                              {config?.marketingServices?.includeNewsletters && <li>Newsletters</li>}
                              {config?.marketingServices?.includeWebDev && <li>Web Dev</li>}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t">
                  <button
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    className="flex items-center px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5 mr-2" />
                    Previous
                  </button>

                  {currentStep < 4 ? (
                    <button
                      onClick={nextStep}
                      className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Next
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </button>
                  ) : (
                    <button
                      onClick={completeWizard}
                      disabled={saving}
                      className="flex items-center px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <Save className="w-5 h-5 mr-2" />
                      {saving ? 'Saving...' : 'Complete & View Overview'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Tabbed Interface - Show after wizard completion */}
            {hasCompletedWizard && (
              <>
                {/* Tab Navigation */}
                <div className="mb-6 border-b border-gray-200">
                  <div className="flex space-x-8">
                    <button
                      onClick={() => setActiveTab('overview')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === 'overview' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      ROI Overview
                    </button>
                    <button
                      onClick={() => setActiveTab('breakdown')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === 'breakdown'
                          ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Detailed Financial Breakdown
                    </button>
                    <button
                      onClick={() => setActiveTab('settings')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === 'settings' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Settings
                    </button>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="bg-white rounded-lg shadow-sm p-8">
                  {/* ROI Overview Tab */}
                  {activeTab === 'overview' && (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Business ROI Overview</h2>
                        <button
                          onClick={restartWizard}
                          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Rerun Wizard
                        </button>
                      </div>

                      {/* Top Metric Cards - Business Budget Style */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {/* Monthly Revenue */}
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-sm border border-green-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-semibold text-green-900 whitespace-nowrap">Monthly Revenue</h3>
                            <DollarSign className="w-5 h-5 text-green-600 flex-shrink-0" />
                          </div>
                          <p className="text-lg font-bold text-green-900">
                            {formatCurrency(breakdown?.revenue, config?.currency)}
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            💡 Current revenue
                          </p>
                        </div>

                        {/* Desired Revenue */}
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-sm border border-blue-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-semibold text-blue-900 whitespace-nowrap">Desired Revenue</h3>
                            <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          </div>
                          <p className="text-lg font-bold text-blue-900">
                            {formatCurrency(breakdown?.desiredRevenue, config?.currency)}
                          </p>
                          <p className="text-xs text-blue-700 mt-1">
                            🎯 Revenue target
                          </p>
                        </div>

                        {/* Total Costs */}
                        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg shadow-sm border border-red-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-semibold text-red-900 whitespace-nowrap">Total Costs</h3>
                            <TrendingDown className="w-5 h-5 text-red-600 flex-shrink-0" />
                          </div>
                          <p className="text-lg font-bold text-red-900">
                            {formatCurrency(breakdown?.totalCosts, config?.currency)}
                          </p>
                          <p className="text-xs text-red-700 mt-1">
                            {((breakdown?.totalCosts / breakdown?.revenue) * 100)?.toFixed(0)}% of revenue
                          </p>
                        </div>

                        {/* Net Profit */}
                        <div className={`bg-gradient-to-br rounded-lg shadow-sm border p-4 ${
                          breakdown?.netProfit >= 0 
                            ? 'from-purple-50 to-purple-100 border-purple-200' :'from-orange-50 to-orange-100 border-orange-200'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <h3 className={`text-xs font-semibold whitespace-nowrap ${
                              breakdown?.netProfit >= 0 ? 'text-purple-900' : 'text-orange-900'
                            }`}>
                              Net Profit
                            </h3>
                            <PieChart className={`w-5 h-5 flex-shrink-0 ${
                              breakdown?.netProfit >= 0 ? 'text-purple-600' : 'text-orange-600'
                            }`} />
                          </div>
                          <p className={`text-lg font-bold ${
                            breakdown?.netProfit >= 0 ? 'text-purple-900' : 'text-orange-900'
                          }`}>
                            {formatCurrency(breakdown?.netProfit, config?.currency)}
                          </p>
                          <p className={`text-xs mt-1 ${
                            breakdown?.netProfit >= 0 ? 'text-purple-700' : 'text-orange-700'
                          }`}>
                            {breakdown?.netProfitMargin?.toFixed(1)}% margin
                          </p>
                        </div>

                        {/* ROI */}
                        <div className={`bg-gradient-to-br rounded-lg shadow-sm border p-4 ${
                          breakdown?.roi >= 0 
                            ? 'from-emerald-50 to-emerald-100 border-emerald-200' :'from-yellow-50 to-yellow-100 border-yellow-200'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <h3 className={`text-xs font-semibold ${
                              breakdown?.roi >= 0 ? 'text-emerald-900' : 'text-yellow-900'
                            }`}>
                              ROI
                            </h3>
                            <Calculator className={`w-5 h-5 flex-shrink-0 ${
                              breakdown?.roi >= 0 ? 'text-emerald-600' : 'text-yellow-600'
                            }`} />
                          </div>
                          <p className={`text-lg font-bold ${
                            breakdown?.roi >= 0 ? 'text-emerald-900' : 'text-yellow-900'
                          }`}>
                            {breakdown?.roi}%
                          </p>
                          <p className={`text-xs mt-1 ${
                            breakdown?.roi >= 0 ? 'text-emerald-700' : 'text-yellow-700'
                          }`}>
                            {breakdown?.roi >= 0 ? '✅ Profitable' : '⚠️ Review costs'}
                          </p>
                        </div>
                      </div>

                      {/* Charts Section - Business Budget Style */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Budget Distribution Pie Chart */}
                        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                          <h3 className="text-lg font-bold text-slate-900 mb-4">Cost Distribution</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <RechartsPie>
                              <Pie
                                data={[
                                  { name: 'Platform Costs', value: breakdown?.platformCosts },
                                  { name: 'Marketing/Retainer', value: breakdown?.marketingCosts },
                                  { name: 'PPC Budget', value: breakdown?.ppcBudget },
                                  { name: 'Shipping', value: breakdown?.shippingCosts },
                                  { name: 'Additional Overheads', value: breakdown?.additionalCosts }
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={90}
                                paddingAngle={3}
                                dataKey="value"
                              >
                                {[
                                  { name: 'Platform Costs', value: breakdown?.platformCosts },
                                  { name: 'Marketing/Retainer', value: breakdown?.marketingCosts },
                                  { name: 'PPC Budget', value: breakdown?.ppcBudget },
                                  { name: 'Shipping', value: breakdown?.shippingCosts },
                                  { name: 'Additional Overheads', value: breakdown?.additionalCosts }
                                ]?.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                                ))}
                              </Pie>
                              <Tooltip 
                                formatter={(value) => formatCurrency(value, config?.currency)}
                                contentStyle={{ 
                                  backgroundColor: 'white', 
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '8px'
                                }}
                              />
                              <Legend />
                            </RechartsPie>
                          </ResponsiveContainer>
                        </div>

                        {/* Cost Breakdown Bar Chart */}
                        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                          <h3 className="text-lg font-bold text-slate-900 mb-4">Cost Breakdown</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={[
                                { name: 'Platform', value: breakdown?.platformCosts },
                                { name: 'Marketing', value: breakdown?.marketingCosts },
                                { name: 'PPC Budget', value: breakdown?.ppcBudget },
                                { name: 'Shipping', value: breakdown?.shippingCosts },
                                { name: 'Additional', value: breakdown?.additionalCosts }
                              ]}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={70} />
                              <YAxis tick={{ fontSize: 11 }} />
                              <Tooltip 
                                formatter={(value) => formatCurrency(value, config?.currency)}
                                contentStyle={{ 
                                  backgroundColor: 'white', 
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '8px'
                                }}
                              />
                              <Bar dataKey="value" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* ROI Health Tips - Business Budget Style */}
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-6">
                        <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
                          <AlertCircle className="w-5 h-5" />
                          ROI Health Insights
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">💰 Revenue Gap</h4>
                            <p className="text-sm text-slate-700 break-words">
                              {breakdown?.revenueGap > 0 
                                ? `⚠️ ${formatCurrency(Math.abs(breakdown?.revenueGap), config?.currency)} below desired revenue. Review marketing strategy.` 
                                : breakdown?.revenueGap < 0 
                                  ? `✅ Exceeded desired revenue by ${formatCurrency(Math.abs(breakdown?.revenueGap), config?.currency)}!` 
                                  : '🎯 Right on target!'}
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">📊 Profit Margin</h4>
                            <p className="text-sm text-slate-700 break-words">
                              {breakdown?.netProfitMargin >= 20 
                                ? `✅ Excellent! ${breakdown?.netProfitMargin?.toFixed(1)}% margin is healthy.` 
                                : breakdown?.netProfitMargin >= 10 
                                  ? `👍 Good! ${breakdown?.netProfitMargin?.toFixed(1)}% margin. Aim for 20%+.` 
                                  : `⚠️ ${breakdown?.netProfitMargin?.toFixed(1)}% margin. Consider cost optimization.`}
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">🎯 ROI Performance</h4>
                            <p className="text-sm text-slate-700 break-words">
                              {breakdown?.roi >= 100 
                                ? `✅ Outstanding! ${breakdown?.roi}% ROI shows excellent efficiency.` 
                                : breakdown?.roi >= 50 
                                  ? `👍 Good! ${breakdown?.roi}% ROI. Keep optimizing costs.` 
                                  : breakdown?.roi > 0 
                                    ? `💡 ${breakdown?.roi}% ROI. Focus on reducing expenses.` 
                                    : `⚠️ Negative ROI. Urgent cost review needed.`}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Detailed Financial Breakdown Tab */}
                  {activeTab === 'breakdown' && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Detailed Financial Breakdown</h2>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Monthly Revenue:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.revenue, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Desired Revenue:</span>
                          <span className="font-semibold text-blue-600">{formatCurrency(breakdown?.desiredRevenue, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Revenue Gap:</span>
                          <span className={`font-semibold ${breakdown?.revenueGap > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                            {formatCurrency(Math.abs(breakdown?.revenueGap), config?.currency)}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Platform Costs:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.platformCosts, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Marketing/Retainer:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.marketingCosts, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">PPC Budget:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.ppcBudget, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Shipping Costs:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.shippingCosts, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Additional Overheads:</span>
                          <span className="font-semibold">{formatCurrency(breakdown?.additionalCosts, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between py-3 border-t-2 border-slate-300">
                          <span className="text-slate-900 font-bold">Total Monthly Expenses:</span>
                          <span className="font-bold text-lg">{formatCurrency(breakdown?.totalCosts, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="text-slate-900 font-bold">Gross Profit:</span>
                          <span className="font-bold text-lg text-blue-600">{formatCurrency(breakdown?.grossProfit, config?.currency)}</span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="text-slate-900 font-bold">Net Profit (After All Costs):</span>
                          <span className={`font-bold text-lg ${breakdown?.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(breakdown?.netProfit, config?.currency)}
                          </span>
                        </div>
                        <div className="flex justify-between py-3 border-t-2 border-slate-300">
                          <span className="text-slate-900 font-bold text-lg">Total Company ROI:</span>
                          <span className={`font-bold text-2xl ${breakdown?.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {breakdown?.roi}%
                          </span>
                        </div>

                        {/* Updated ROAS Section with Performance Indicators */}
                        <div className="mt-6 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-300 p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-bold text-indigo-900">Current ROAS (PPC Performance)</h3>
                            <span className={`font-bold text-3xl ${breakdown?.roas >= 4 ? 'text-green-600' : breakdown?.roas >= 2 ? 'text-blue-600' : 'text-orange-600'}`}>
                              {breakdown?.roas}:1
                            </span>
                          </div>
                          
                          {/* Performance Indicator */}
                          <div className={`px-4 py-3 rounded-lg ${
                            breakdown?.roas >= 4 ? 'bg-green-100 border border-green-300' : 
                            breakdown?.roas >= 2 ? 'bg-blue-100 border border-blue-300': 'bg-orange-100 border border-orange-300'
                          }`}>
                            <p className={`text-sm font-semibold ${
                              breakdown?.roas >= 4 ? 'text-green-800' : 
                              breakdown?.roas >= 2 ? 'text-blue-800': 'text-orange-800'
                            }`}>
                              {breakdown?.roas >= 4 
                                ? '✅ Excellent PPC Performance! Your ads are generating strong returns.' 
                                : breakdown?.roas >= 2 
                                  ? '👍 Good PPC Performance! Your ads are performing well and generating positive returns.' 
                                  : '⚠️ PPC Needs Optimization. Your ads aren\'t generating enough revenue to cover costs effectively.'}
                            </p>
                          </div>

                          {/* Explanation */}
                          <div className="mt-3 bg-white rounded-lg p-3 border border-indigo-200">
                            <p className="text-xs text-indigo-800 font-medium">
                              💡 <strong>What this means:</strong> For every R1 spent on PPC ads, you're generating R{breakdown?.roas} in revenue. 
                              {breakdown?.roas >= 4 
                                ? ' This is an excellent return that shows your PPC campaigns are highly effective.' 
                                : breakdown?.roas >= 2 
                                  ? ' This is a solid return that shows your PPC campaigns are working well.' 
                                  : ' You may want to review your ad targeting, bidding strategy, or landing pages to improve returns.'}
                            </p>
                          </div>

                          {/* Important Note */}
                          <div className="mt-3 bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                            <p className="text-xs text-yellow-800 font-medium">
                              ⚠️ <strong>Important:</strong> ROAS only measures PPC efficiency - it doesn't account for shipping costs ({formatCurrency(breakdown?.shippingCosts, config?.currency)}) and overheads ({formatCurrency(breakdown?.additionalCosts, config?.currency)}). 
                              {breakdown?.roi < 0 && breakdown?.roas >= 2 
                                ? ' Your PPC is performing well, but overall profitability is affected by other costs. Review your shipping and overhead expenses.' 
                                : ' For complete business profitability, always refer to your Total Company ROI above.'}
                            </p>
                          </div>
                        </div>
                        
                        {/* 20% Comfortable Profit Target */}
                        <div className="mt-6 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border border-amber-300 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-bold text-amber-900">20% Comfortable Target to Make Profit</h3>
                            <span className={`text-lg font-bold ${breakdown?.meetsComfortableTarget ? 'text-green-600' : 'text-orange-600'}`}>
                              {breakdown?.netProfitMargin?.toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-amber-200 rounded-full h-3 mb-2">
                            <div 
                              className={`h-3 rounded-full transition-all duration-500 ${
                                breakdown?.meetsComfortableTarget ? 'bg-green-500' : 'bg-orange-500'
                              }`}
                              style={{ 
                                width: `${Math.min((breakdown?.netProfitMargin / breakdown?.comfortableProfitTarget) * 100, 100)}%` 
                              }}
                            />
                          </div>
                          <p className="text-xs text-amber-800">
                            {breakdown?.meetsComfortableTarget 
                              ? `✅ Excellent! Your net profit margin of ${breakdown?.netProfitMargin?.toFixed(1)}% exceeds the comfortable 20% target.` 
                              : `⚠️ Your net profit margin is ${breakdown?.netProfitMargin?.toFixed(1)}%. Aim for 20%+ for a comfortable profit cushion.`}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Settings Tab */}
                  {activeTab === 'settings' && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Configuration Settings</h2>
                      
                      {/* Business Metrics */}
                      <div className="border-b pb-4">
                        <h4 className="font-medium text-gray-900 mb-3">Business Metrics</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Profit Margin %</label>
                            <input
                              type="number"
                              value={config?.profitMarginPercentage || ''}
                              onChange={(e) => updateConfig({ profitMarginPercentage: parseFloat(e?.target?.value) || 0 })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Revenue</label>
                            <input
                              type="text"
                              value={formatNumberWithCommas(config?.monthlyRevenue)}
                              onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'monthlyRevenue')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Desired Revenue</label>
                            <input
                              type="text"
                              value={formatNumberWithCommas(config?.desiredRevenue)}
                              onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'desiredRevenue')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Shipping Cost</label>
                            <input
                              type="text"
                              value={formatNumberWithCommas(config?.monthlyShippingCost)}
                              onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'monthlyShippingCost')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Platform Settings */}
                      <div className="border-b pb-4">
                        <h4 className="font-medium text-gray-900 mb-3">Platform Settings</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                            <select
                              value={config?.platformSettings?.platform}
                              onChange={(e) => updatePlatformSettings({ platform: e?.target?.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="woocommerce">WooCommerce</option>
                              <option value="shopify">Shopify</option>
                            </select>
                          </div>
                          {config?.platformSettings?.platform === 'woocommerce' && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Hosting Cost</label>
                                <input
                                  type="text"
                                  value={formatNumberWithCommas(config?.platformSettings?.hostingCost)}
                                  onChange={(e) => {
                                    const value = parseNumberWithCommas(e?.target?.value);
                                    updatePlatformSettings({ hostingCost: value });
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Gateway Fee %</label>
                                <input
                                  type="number"
                                  value={config?.platformSettings?.paymentGatewayFeePercentage || ''}
                                  onChange={(e) => updatePlatformSettings({ paymentGatewayFeePercentage: parseFloat(e?.target?.value) || 0 })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </>
                          )}
                          {config?.platformSettings?.platform === 'shopify' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Shopify Plan</label>
                              <select
                                value={config?.platformSettings?.shopifyPlan || ''}
                                onChange={(e) => handleShopifyPlanChange(e?.target?.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select Plan</option>
                                {Object.entries(shopifyPlans)?.map(([key, plan]) => (
                                  <option key={key} value={key}>{plan?.label}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Marketing Settings */}
                      <div className="border-b pb-4">
                        <h4 className="font-medium text-gray-900 mb-3">Marketing Settings</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Management Type</label>
                            <select
                              value={config?.marketingServices?.marketingManagementType}
                              onChange={(e) => updateMarketingServices({ marketingManagementType: e?.target?.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="self_managed">Self Managed</option>
                              <option value="agency_managed">Agency Managed</option>
                            </select>
                          </div>
                          {config?.marketingServices?.marketingManagementType === 'agency_managed' && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Retainer</label>
                                <input
                                  type="text"
                                  value={formatNumberWithCommas(config?.marketingServices?.monthlyRetainer)}
                                  onChange={(e) => {
                                    const value = parseNumberWithCommas(e?.target?.value);
                                    updateMarketingServices({ monthlyRetainer: value });
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">PPC Budget</label>
                                <input
                                  type="text"
                                  value={formatNumberWithCommas(config?.totalPpcBudget)}
                                  onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'totalPpcBudget')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Overheads</label>
                            <input
                              type="text"
                              value={formatNumberWithCommas(config?.marketingServices?.additionalOverheads)}
                              onChange={(e) => {
                                const value = parseNumberWithCommas(e?.target?.value);
                                updateMarketingServices({ additionalOverheads: value });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          className="flex items-center px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}